import { tokens } from '@/theme'
import { OUTCOME_LABEL, STAGE_LABEL, isClosed, type StageCode } from '@/domain/funnel'

interface Props {
  stage: StageCode
  size?: 'sm' | 'md'
}

interface PillTone {
  fg: string
  bg: string
  border: string
}

/**
 * Stage badge for an initiative.
 *
 * Close is one stage whose outcome is a field inside it (design v2, section
 * 2), so both closed codes share the dark grey of Close and carry Succeeded or
 * Failed after it. The outcome still comes from the stage code, so the pill
 * takes one value rather than two that could disagree.
 */
const CLOSE_TONE: PillTone = { fg: tokens.paper, bg: tokens.ink2, border: tokens.ink2 }

const STAGE_TONES: Record<StageCode, PillTone> = {
  shaped: {
    fg: tokens.statusOrange,
    bg: tokens.statusOrangeWash,
    border: tokens.statusOrangeLine,
  },
  allocate: {
    fg: tokens.statusTeal,
    bg: tokens.statusTealWash,
    border: tokens.statusTealLine,
  },
  in_progress: {
    fg: tokens.statusGreen,
    bg: tokens.statusGreenWash,
    border: tokens.statusGreenLine,
  },
  closed_success: CLOSE_TONE,
  closed_fail: CLOSE_TONE,
}

export function StatusPill({ stage, size = 'sm' }: Props) {
  const tone = STAGE_TONES[stage]

  const padding = size === 'sm' ? '3px 9px' : '5px 12px'
  const fontSize = size === 'sm' ? 10.5 : 12

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding,
        fontSize,
        fontFamily: tokens.fontDisplay,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tone.fg,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: tokens.radiusSm,
        whiteSpace: 'nowrap',
      }}
    >
      {isClosed(stage) ? 'Close' : STAGE_LABEL[stage]}
      {isClosed(stage) && (
        <span style={{ fontSize: fontSize - 1.5, opacity: 0.85 }}>
          · {OUTCOME_LABEL[stage as keyof typeof OUTCOME_LABEL]}
        </span>
      )}
    </span>
  )
}
